import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import CounterInput from 'components/Common/Form/Input/CounterInput'
import BodyText from 'LuxKit/Typography/BodyText'
import noop from 'lib/function/noop'
import { rem } from 'polished'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import RadioInput from 'LuxKit/Radio/RadioInput'
import { getValidTransferOptions } from './ExperienceTransferUtils'

const FormGroup = styled(Group)`
  max-width: ${rem(422)};
`

interface Props {
  transfer: App.ExperienceTransferView
  options: Array<App.ExperienceTransferOption>
  onChange?: (transfer: App.ExperienceTransferView) => void
  name?: string
  maxBaggageOverride?: number
}

function ExperienceDetailsTransferBookingBaggage(props: Props) {
  const { transfer, onChange = noop, name, options, maxBaggageOverride } = props
  const intl = useIntl()

  const { baggage } = transfer

  const [hasOversized, setHasOversized] = useState<boolean>(!!baggage.oversizedBags)

  const maxBaggage = useMemo(() => {
    if (maxBaggageOverride !== undefined) return maxBaggageOverride
    const transfers = getValidTransferOptions(transfer, options)
    return Math.max(...transfers.map(option => option.maxCheckedBags))
  }, [options, transfer, maxBaggageOverride])

  const onCounterChange = (value: number, input: HTMLInputElement) => {
    const field = input.dataset.field as keyof App.OrderTransferItemBaggage
    onChange({
      ...transfer,
      baggage: {
        ...baggage,
        [field]: value,
      },
    })
  }

  const onOversizedBagsChange = (value: boolean) => {
    setHasOversized(value)

    // Reset oversized amount if user chooses "No"
    if (!value) {
      onChange({
        ...transfer,
        baggage: {
          ...baggage,
          oversizedBags: 0,
        },
      })
    }
  }

  return (
    <FormGroup direction="vertical" gap={24}>
      <Group
        direction="vertical"
        tabletDirection="horizontal"
        verticalAlign="center"
        horizontalAlign="start"
        tabletHorizontalAlign="space-between"
        gap={8}
      >
        <BodyText variant="medium" weight="bold">
          {intl.formatMessage({
            id: 'ybufhgsqLY',
            defaultMessage: 'How many bags do you have?',
            description: 'Question label for bag count in transfer booking baggage section',
          })}
        </BodyText>
        <CounterInput
          direction="vertical"
          min={0}
          value={baggage.bags}
          onChange={onCounterChange}
          data-field="bags"
          name={`${name}.baggage.bags`}
          max={maxBaggage}
        />
      </Group>

      <Group
        direction="vertical"
        tabletDirection="horizontal"
        verticalAlign="center"
        horizontalAlign="space-between"
        gap={8}
      >
        <BodyText variant="medium" weight="bold">
          {intl.formatMessage({
            id: 'cZHYC+0E45',
            defaultMessage: 'Do you have any oversized bags?',
            description: 'Question label for oversized bags in transfer booking baggage section',
          })}
        </BodyText>
        <Group direction="horizontal" gap={16}>
          <RadioInput
            checked={hasOversized}
            onChange={() => onOversizedBagsChange(true)}
          >
            {intl.formatMessage({
              id: 'OmluJbjYbe',
              defaultMessage: 'Yes',
              description: 'Affirmative radio option for oversized bags question in transfer booking',
            })}
          </RadioInput>
          <RadioInput
            checked={!hasOversized}
            onChange={() => onOversizedBagsChange(false)}
          >
            {intl.formatMessage({
              id: 'W1xcofJaqM',
              defaultMessage: 'No',
              description: 'Negative radio option for oversized bags question in transfer booking',
            })}
          </RadioInput>
        </Group>
      </Group>

      {hasOversized && (
        <Group
          direction="vertical"
          tabletDirection="horizontal"
          verticalAlign="center"
          horizontalAlign="start"
          tabletHorizontalAlign="space-between"
          gap={8}
        >
          <BodyText variant="medium" weight="bold">
            {intl.formatMessage({
              id: 'lBq+uI9NIR',
              defaultMessage: 'How many oversized bags do you have?',
              description: 'Question label for oversized bag count in transfer booking baggage section',
            })}
          </BodyText>
          <CounterInput
            direction="vertical"
            min={1}
            value={baggage.oversizedBags}
            onChange={onCounterChange}
            data-field="oversizedBags"
            name={`${name}.baggage.oversizedBags`}
            max={baggage.bags}
          />
        </Group>
      )}
    </FormGroup>
  )
}

export default ExperienceDetailsTransferBookingBaggage
